import { useCallback, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';

import type {
  ColumnFiltersState,
  ColumnVisibilityState,
  Row,
  TableState
} from '@tanstack/react-table';
import { type DataTableFeatures, type DataTableRowPresentation, useAppTable } from 'design-system';
import { useLocation } from 'react-router';

import { SHOW_SELECTED_URL_PARAM } from '@/config/constants';
import { useAppStore } from '@/store/hooks';
import { defaultVisibilityState, FILTER_KEYS } from '../constants';
import { canExpandRow } from '../lib/canExpandRow';
import { findToggledFilterValue } from '../lib/columnFiltersDiff';
import { getCatalogRowId } from '../lib/getCatalogRowId';
import { useFilter } from '../state/useFilter';

import { useCatalogColumnFilters } from './useCatalogColumnFilters';
import { useCatalogColumns } from './useCatalogColumns';
import { useCatalogData } from './useCatalogData';
import { useCatalogSelectionAdapter } from './useCatalogSelectionAdapter';
import { useCatalogSorting } from './useCatalogSorting';
import { useCodeFilteredData } from './useCodeFilteredData';
import { useFilteredCatalogData } from './useFilteredCatalogData';
import { usePeriodGroups } from './usePeriodGroups';
import { useSearchColumnFilter } from './useSearchColumnFilter';

import type { CatalogItem } from '@/entities/catalog-item';

const FILTERABLE_COLUMN_IDS = Object.values(FILTER_KEYS);
const EMPTY_EXPANDED_STATE = {};

// Expansion is consumed by the virtualized row subscriptions. Keeping that
// slice stable here avoids rebuilding the full catalog row model per click.
const selectCatalogOwnerTableState = (state: TableState<DataTableFeatures>) => ({
  ...state,
  expanded: EMPTY_EXPANDED_STATE
});

export interface UseCatalogOptions {
  isForwardCurves?: boolean;
  isInDashboard: boolean;
  maxSelectedSymbols?: number;
  renderFactsheetButton?: (code: string, mode: 'modal' | 'newTab') => ReactNode;
}

const useCatalog = ({
  isForwardCurves = false,
  isInDashboard,
  maxSelectedSymbols,
  renderFactsheetButton
}: UseCatalogOptions) => {
  const location = useLocation();
  const { filters, disableSearchParams, toggleFilterOption } = useFilter();
  const store = useAppStore();

  // Freeze the visible row-id set the moment the toggle turns ON; ignore selection
  // changes while the toggle is active so clearing + re-selecting doesn't shift the
  // view. Applied as a `rows` override below rather than a table column filter.
  const frozenRowIdsRef = useRef<string[]>([]);
  const prevShowSelectedRef = useRef(false);
  const showSelectedOnly = filters[SHOW_SELECTED_URL_PARAM];
  if (showSelectedOnly && !prevShowSelectedRef.current) {
    frozenRowIdsRef.current = store.getState().symbolSelector.symbols.map(getCatalogRowId);
  }
  prevShowSelectedRef.current = showSelectedOnly;

  const selectionAdapter = useCatalogSelectionAdapter(isForwardCurves, maxSelectedSymbols);
  const columns = useCatalogColumns({
    isForwardCurves,
    isInDashboard,
    renderFactsheetButton,
    selectionAdapter
  });

  const [activeTab, setActiveTab] = useState<number>(isForwardCurves ? 1 : 0);
  const [showDiscontinued, setShowDiscontinued] = useState<boolean>(false);
  const [expandedData, setExpandedData] = useState<boolean>(false);
  const [forwardPeriodsInline, setForwardPeriodsInline] = useState<boolean>(true);
  const [filterOpen, setFilterOpen] = useState<boolean>(false);
  const [displaySettingsOpen, setDisplaySettingsOpen] = useState<boolean>(false);
  const [columnVisibility, setColumnVisibility] =
    useState<ColumnVisibilityState>(defaultVisibilityState);

  const { catalog, status } = useCatalogData(isForwardCurves);
  const { sorting, onSortingChange } = useCatalogSorting(
    isForwardCurves,
    disableSearchParams,
    filters.search
  );
  const facetColumnFilters = useCatalogColumnFilters({ filters, showDiscontinued });

  const clearState = useEffectEvent(() => {
    setFilterOpen(false);
    setColumnVisibility(prevState => ({
      ...prevState,
      TimeRef: isForwardCurves || expandedData,
      PeriodType: isForwardCurves || expandedData
    }));
  });

  useEffect(() => {
    // oxlint-disable-next-line react-hooks-js/set-state-in-effect
    clearState();
    setActiveTab(isForwardCurves ? 1 : 0);
  }, [location.pathname, isForwardCurves]);

  const filteredData = useFilteredCatalogData(catalog, expandedData, isForwardCurves);
  const codeFilteredData = useCodeFilteredData(filteredData, filters.code);
  const periodGroups = usePeriodGroups(catalog, showDiscontinued, isForwardCurves);
  const searchColumnFilter = useSearchColumnFilter(codeFilteredData, filters.search);
  const columnFilters = useMemo(
    () => [...facetColumnFilters, ...searchColumnFilter],
    [facetColumnFilters, searchColumnFilter]
  );

  const getRowCanExpand = useCallback(
    (row: Row<DataTableFeatures, CatalogItem>) =>
      canExpandRow({
        forwardPeriodsInline,
        isForwardCurves,
        expandedData,
        periodCount: periodGroups.get(row.original.Code)?.length ?? 0
      }),
    [forwardPeriodsInline, isForwardCurves, expandedData, periodGroups]
  );

  // `DataTableFilterPanel`/`DataTableActiveFilterChips` write straight to
  // `column.setFilterValue()`, but `useFilter()`'s URL state stays the single
  // source of truth (see `filter.context.tsx`) — recover the one value the
  // user toggled and replay it as `toggleFilterOption`, rather than letting
  // TanStack's column-filters state diverge from the URL.
  const onColumnFiltersChange = useCallback(
    (updater: ColumnFiltersState | ((old: ColumnFiltersState) => ColumnFiltersState)) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater;
      const toggled = findToggledFilterValue(columnFilters, next, FILTERABLE_COLUMN_IDS);
      if (toggled) toggleFilterOption(toggled.columnId, toggled.value);
    },
    [columnFilters, toggleFilterOption]
  );

  const table = useAppTable(
    {
      data: codeFilteredData,
      columns,
      getRowId: getCatalogRowId,
      state: { columnVisibility, sorting, columnFilters },
      onColumnVisibilityChange: setColumnVisibility,
      onSortingChange,
      onColumnFiltersChange,
      getRowCanExpand
    },
    selectCatalogOwnerTableState
  );

  const allRows = table.getRowModel().rows;
  const rows = showSelectedOnly
    ? (() => {
        const frozenSet = new Set(frozenRowIdsRef.current);
        return allRows.filter(row => frozenSet.has(row.id));
      })()
    : allRows;

  const getRowPresentation = useCallback(
    (row: Row<DataTableFeatures, CatalogItem>): DataTableRowPresentation => {
      const snapshot = selectionAdapter.getRowState(row);
      const selected = snapshot.checked || snapshot.indeterminate;
      let tone: DataTableRowPresentation['tone'] = 'default';

      if (snapshot.disabled) {
        tone = 'restricted';
      } else if (row.getIsExpanded()) {
        tone = 'expanded';
      } else if (selected) {
        tone = isForwardCurves ? 'selected-secondary' : 'selected-primary';
      }

      return {
        tone,
        disabled: snapshot.disabled,
        interactive: !snapshot.disabled
      };
    },
    [isForwardCurves, selectionAdapter]
  );

  const onRowActivate = useCallback(
    (row: Row<DataTableFeatures, CatalogItem>) => selectionAdapter.toggleRow(row),
    [selectionAdapter]
  );

  return {
    status,
    filterOpen,
    setFilterOpen,
    displaySettingsOpen,
    setDisplaySettingsOpen,
    filteredData,
    table,
    rows,
    getRowPresentation,
    onRowActivate,
    activeTab,
    setActiveTab,
    isForwardCurves,
    expandedData,
    setExpandedData,
    forwardPeriodsInline,
    setForwardPeriodsInline,
    periodGroups,
    showDiscontinued,
    setShowDiscontinued
  };
};

export default useCatalog;
